import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TextEditable from 'components/text-editable/text-editable';
import PrioritySelector from 'components/priority-select/priority-select';
import { connect, useDispatch, useSelector } from 'react-redux';
import { AccessLevel, Permission } from 'services/permission.model';
import { PermissionGuardSelectors } from 'modules/permission-guard/permission-guard.controller';
import MaxLettersWarning from 'components/max-letters-warning/max-letters-warning';
import {
  Actions as ProductionWorkflowAdditionalTasksActions,
  ProductionWorkflowTaskTWithIssues,
} from 'pages/production-workflow/controllers/production-workflow-additional-tasks.controller';
import { Actions as ManageFailedTasksActions } from 'modules/manage-failed-tasks-modal/manage-failed-tasks.controller';
import { ProductionWorkflowTaskT } from 'services/production-workflow.model';
import ActionsMenu from 'pages/production-workflow/modules/additional-task-item/components/action-menu/actions-menu';
import { Paths } from 'routes/paths';
import { AppState } from 'redux/store';
import { Checkbox, FormControlLabel, Stack, Switch } from '@mui/material';
import NestedFinishedAdditionalTaskItem from 'modules/manage-failed-tasks-modal/components/nested-finished-additional-task/nested-finished-additional-task';
import { TaskStatusEnum } from 'types/status-enums';
import s from './finished-additional-task-item.module.scss';

type OwnProps = {
  highlightName?: string;
  isProcessingGuard: boolean;
  taskInfo: ProductionWorkflowTaskTWithIssues;
};

type DispatchProps = {
  closeManageFailedTasksModal: () => void;
  copyAdditionalTaskLink: (taskId: string) => void;
  deleteTemporaryAdditionalTask: (id: string) => void;
  openDeleteConfirmationModal: (taskId: string) => void;
  updateAdditionalTask: (id: string, value: Partial<ProductionWorkflowTaskT>) => void;
};
type Props = DispatchProps & OwnProps;

const FinishedAdditionalTaskItem: FC<Props> = ({
  taskInfo,
  highlightName,
  isProcessingGuard,
  updateAdditionalTask,
  copyAdditionalTaskLink,
  openDeleteConfirmationModal,
  deleteTemporaryAdditionalTask,
  closeManageFailedTasksModal,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isTemporaryTask = useMemo(() => taskInfo?.id?.split('-')?.[0] === 'new', [taskInfo?.id]);

  const handleOnEditEnd = (value: string) => {
    return updateAdditionalTask(taskInfo.id, { name: value });
  };

  const handleOnEditModeEnd = (value: string) => {
    if (!value || isTemporaryTask) return deleteTemporaryAdditionalTask(taskInfo.id);

    return '';
  };

  const accessLevels = [AccessLevel.userDepartment, AccessLevel.userDepartmentSubDepartments, AccessLevel.allDepartments];
  const departmentsAll = taskInfo.departmets_all;
  const isEditPermitted = useSelector((state: AppState) =>
    PermissionGuardSelectors.checkEditLevel(state, Permission.webTaskEditLevel, accessLevels, departmentsAll),
  );

  const handleNavigateToItem = () => {
    navigate(`${Paths.Task}/${taskInfo.id}`);
    closeManageFailedTasksModal();
  };

  const isManageFailedTasks = 'isFail' in taskInfo;

  const isFail = isManageFailedTasks ? taskInfo.isFail : undefined;
  const isWithMoney = 'isWithMoney' in taskInfo ? taskInfo.isWithMoney : undefined;
  const disableNestedAdditionalItem = taskInfo.status !== TaskStatusEnum.Done;

  return (
    <Stack direction="column" gap="12px" justifyContent="space-between" className={s.nested_content_collapse}>
      <Stack direction="row" alignItems="center" gap="6px" justifyContent="space-between">
        <div
          className={taskInfo.issues?.length ? `${s.container} ${s.container_with_warning}` : s.container}
          onClick={() => {
            if (!isTemporaryTask) {
              handleNavigateToItem();
            }
          }}
        >
          <TextEditable
            inputMinWidth={250}
            allowedEmpty={false}
            textClassName={s.title}
            style={{ width: '250px' }}
            focused={isTemporaryTask}
            value={taskInfo.name || ''}
            highlitedValue={highlightName}
            placeholderClassName={s.placeholder_name}
            textValidationTitle={<MaxLettersWarning />}
            onEditEnd={handleOnEditEnd}
            isNotClickable={!isEditPermitted}
            onClick={(e) => e.stopPropagation()}
            onEditModeEnd={handleOnEditModeEnd}
          />
          {!isTemporaryTask && (
            <>
              {'taskResponsibilities' in taskInfo &&
              Array.isArray(taskInfo.taskResponsibilities) &&
              taskInfo?.taskResponsibilities.length ? (
                <div>
                  <span className={s.responsibility}>
                    Responsibilities: <strong>{taskInfo?.taskResponsibilities.length}</strong>
                  </span>
                </div>
              ) : (
                <div />
              )}

              <PrioritySelector
                priority={taskInfo.priority}
                isProcessingGuard={isProcessingGuard}
                isEditPermitted={isEditPermitted}
                onSelect={(value: string) => updateAdditionalTask(taskInfo.id, { priority: value })}
              />
              <ActionsMenu
                itemId={taskInfo.id}
                taskDepartments={departmentsAll}
                buttonClassName={s.action_button}
                isProcessingGuard={isProcessingGuard}
                isTaskFromRootHierarchy={taskInfo.failed_task_id}
                isRootAdditionalTask={taskInfo?.reopenedTasks && taskInfo.reopenedTasks?.length > 0}
                copyAdditionalTaskLink={copyAdditionalTaskLink}
                openDeleteConfirmationModal={openDeleteConfirmationModal}
              />
            </>
          )}
        </div>
        <Stack direction="row" alignItems="center" gap="30px">
          {isManageFailedTasks && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="medium"
                    color="error"
                    checked={isFail || taskInfo.disable || disableNestedAdditionalItem}
                    disabled={!isEditPermitted || taskInfo.disable || disableNestedAdditionalItem}
                    sx={{
                      color:
                        !isEditPermitted || taskInfo.disable || disableNestedAdditionalItem ? '#b0b0b0 !important' : undefined,
                      '&.Mui-disabled': {
                        color: '#b0b0b0 !important',
                      },
                      '&.Mui-disabled.Mui-checked': {
                        color: '#b0b0b0 !important',
                      },
                    }}
                    onChange={() =>
                      dispatch(
                        ProductionWorkflowAdditionalTasksActions.handleFailAdditionalTask(
                          taskInfo.id,
                          {
                            isFail: !isFail,
                            isWithMoney: !isFail && isWithMoney,
                          },
                          taskInfo.failed_task_id,
                        ),
                      )
                    }
                  />
                }
                label=""
              />
              <Stack direction="row" alignItems="center" gap="6px">
                <FormControlLabel
                  control={
                    <Switch
                      checked={isWithMoney || taskInfo.disable || disableNestedAdditionalItem}
                      disabled={!isEditPermitted || taskInfo.disable || disableNestedAdditionalItem}
                      // eslint-disable-next-line react/jsx-props-no-multi-spaces
                      onChange={() =>
                        dispatch(
                          ProductionWorkflowAdditionalTasksActions.handleFailAdditionalTask(
                            taskInfo.id,
                            {
                              isWithMoney: !isWithMoney,
                              isFail: !isFail && !isWithMoney ? true : isFail,
                            },
                            taskInfo.failed_task_id,
                          ),
                        )
                      }
                    />
                  }
                  label=""
                />
              </Stack>
            </>
          )}
        </Stack>
      </Stack>

      {taskInfo.reopenedTask && (
        <NestedFinishedAdditionalTaskItem
          taskInfo={taskInfo?.reopenedTask}
          highlightName={highlightName}
          isProcessingGuard={isProcessingGuard}
        />
      )}
    </Stack>
  );
};

const mapDispatchToProps: DispatchProps = {
  updateAdditionalTask: ProductionWorkflowAdditionalTasksActions.updateAdditionalTask,
  copyAdditionalTaskLink: ProductionWorkflowAdditionalTasksActions.copyAdditionalTaskLink,
  openDeleteConfirmationModal: ProductionWorkflowAdditionalTasksActions.openDeleteConfirmationModal,
  deleteTemporaryAdditionalTask: ProductionWorkflowAdditionalTasksActions.deleteTemporaryAdditionalTask,
  closeManageFailedTasksModal: ManageFailedTasksActions.closeManageFailedTasksModal,
};

export default connect(null, mapDispatchToProps)(FinishedAdditionalTaskItem);
